import React, { useCallback, useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import withAuth from '../common/auth';
import {
  BASE_URL,
  GET_DENOMINATIONS,
  POST_DAY_CLOSE,
  GET_COLLECTION_SUMMARY,
} from '../utlilities/config';
import { ToastContainer, toast, Slide } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FiSave,
  FiBarChart2,
} from 'react-icons/fi';
import { getWebUserInfo, initializeWebUserInfo } from '../utlilities/webConfig';

const todayStr = () => new Date().toISOString().slice(0, 10);

const Spinner = ({ label = 'Loading...' }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-white bg-opacity-70 backdrop-blur-sm">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-indigo-600"></div>
      <p className="text-indigo-700 text-sm font-medium">{label}</p>
    </div>
  </div>
);

const getTokenHeaders = (token) => ({
  XApiKey: process.env.REACT_APP_API_KEY,
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const normalizeDenominations = (response) => {
  const root = response?.data;
  const list = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : [];
  return list
    .filter((item) => item?.IsActive !== false && item?.isActive !== false)
    .map((item) => ({
      denominationID:
        item?.DenominationID ?? item?.denominationID ?? item?.denominationId ?? 0,
      denominationValue: Number(item?.DenominationValue ?? item?.denominationValue ?? 0),
      quantity: 0,
      amount: 0,
    }))
    .sort((a, b) => b.denominationValue - a.denominationValue);
};

const SUMMARY_DATA_KEYS = [
  'lab',
  'iP_Lab',
  'op',
  'generalBill',
  'pharmacy',
  'salesReturn',
  'receipt',
  'expense',
  'iP_Pharmacy',
  'iP_Sales_Return',
  'iP_Billing',
];

const toNullableNumber = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const isCreditSummary = (key, item) => {
  const docType = item?.DocType ?? item?.docType ?? '';
  return /credit/i.test(String(key)) || /credit/i.test(String(docType));
};

const buildCollectionSummaryDetails = (data) => {
  if (!data || typeof data !== 'object') return [];
  const rows = [];
  for (const key of SUMMARY_DATA_KEYS) {
    const arr = data[key];
    if (!Array.isArray(arr) || arr.length === 0) continue;
    const item = arr[0];
    if (isCreditSummary(key, item)) continue;
    rows.push({
      docType: item?.DocType ?? item?.docType ?? null,
      totalAmount: toNullableNumber(item?.TotalAmount ?? item?.totalAmount),
      balanceDue: toNullableNumber(item?.BalanceDue ?? item?.balanceDue),
      cashAmount: toNullableNumber(item?.CashAmount ?? item?.cashAmount),
      bankAmount: toNullableNumber(item?.BankAmount ?? item?.bankAmount),
    });
  }
  return rows;
};

const readReceiptSystemAmounts = (data) => {
  const receipt = Array.isArray(data?.receipt) && data.receipt.length ? data.receipt[0] : null;
  const systemCashAmount = Number(receipt?.CashAmount ?? receipt?.cashAmount ?? 0) || 0;
  const systemBankAmount = Number(receipt?.BankAmount ?? receipt?.bankAmount ?? 0) || 0;
  return { systemCashAmount, systemBankAmount };
};

const isApiFailed = (root) =>
  root?.isSucess === false || root?.isSuccess === false || root?.IsSuccess === false;

const normalizeClosings = (response) => {
  const root = response?.data;
  const list = Array.isArray(root?.data) ? root.data : Array.isArray(root) ? root : [];
  return list.filter(Boolean);
};

const parseCollectionSummaryJson = (raw) => {
  if (!raw || typeof raw !== 'string') return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => !isCreditSummary('', item))
      .map((item) => ({
        docType: item?.DocType ?? item?.docType ?? null,
        totalAmount: toNullableNumber(item?.TotalAmount ?? item?.totalAmount),
        balanceDue: toNullableNumber(item?.BalanceDue ?? item?.balanceDue),
        cashAmount: toNullableNumber(item?.CashAmount ?? item?.cashAmount),
        bankAmount: toNullableNumber(item?.BankAmount ?? item?.bankAmount),
      }));
  } catch (_) {
    return [];
  }
};

const findMatchingClosing = (closings, userID) => {
  if (!Array.isArray(closings) || closings.length === 0 || !Number.isFinite(userID)) return null;
  const matches = closings.filter((row) => Number(row?.UserID) === userID);
  if (!matches.length) return null;
  return matches.sort((a, b) => {
    const left = new Date(a?.ClosingDateTime ?? a?.CreatedDate ?? 0).getTime() || Number(a?.ClosingID) || 0;
    const right =
      new Date(b?.ClosingDateTime ?? b?.CreatedDate ?? 0).getTime() || Number(b?.ClosingID) || 0;
    return right - left;
  })[0];
};

const findClosingByShift = (closings, userID, shiftID) => {
  if (!Array.isArray(closings) || closings.length === 0 || !Number.isFinite(userID)) return null;
  const matches = closings.filter(
    (row) => Number(row?.UserID) === userID && Number(row?.ShiftID ?? row?.shiftID) === Number(shiftID)
  );
  if (!matches.length) return null;
  return matches.sort((a, b) => {
    const left = new Date(a?.ClosingDateTime ?? a?.CreatedDate ?? 0).getTime() || Number(a?.ClosingID) || 0;
    const right =
      new Date(b?.ClosingDateTime ?? b?.CreatedDate ?? 0).getTime() || Number(b?.ClosingID) || 0;
    return right - left;
  })[0];
};

const prefillDenominationsFromDetails = (baseRows, details) => {
  if (!Array.isArray(baseRows) || baseRows.length === 0) return [];
  if (!Array.isArray(details) || details.length === 0) return baseRows;

  const detailsByID = new Map(
    details.map((row) => [Number(row?.DenominationID ?? row?.denominationID ?? 0), row])
  );
  const detailsByValue = new Map(
    details.map((row) => [Number(row?.DenominationValue ?? row?.denominationValue ?? 0), row])
  );

  return baseRows.map((row) => {
    const byID = detailsByID.get(Number(row?.denominationID));
    const byValue = detailsByValue.get(Number(row?.denominationValue));
    const matched = byID || byValue;
    if (!matched) return row;
    const quantity = Number(matched?.Quantity ?? matched?.quantity ?? 0) || 0;
    const amount = Number(matched?.Amount ?? matched?.amount ?? row.denominationValue * quantity) || 0;
    return { ...row, quantity, amount };
  });
};

const DayClose = () => {
  const token = localStorage.getItem('accessToken');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [closingDate, setClosingDate] = useState(todayStr);
  const [sessionLoaded, setSessionLoaded] = useState(false);

  const [denominations, setDenominations] = useState([]);
  const [shift1Denominations, setShift1Denominations] = useState([]);
  const [collectionSummaryDetails, setCollectionSummaryDetails] = useState([]);

  const [userInfo, setUserInfo] = useState({});

  const [form, setForm] = useState({
    bankAmount: 0,
    systemCashAmount: 0,
    systemBankAmount: 0,
    systemCardAmount: 0,
    cardAmount: 0,
    remarks: '',
  });
  const [shift1BankAmount, setShift1BankAmount] = useState(0);
  const [hasShift1Closing, setHasShift1Closing] = useState(false);

  const isElectron = () => window && window.process && window.process.type;

  let ipcRenderer;
  if (isElectron()) {
    ipcRenderer = window.require('electron').ipcRenderer;
  }

  useEffect(() => {
    const fetchData = async () => {
      if (isElectron()) {
        const userinfo = await ipcRenderer.invoke('store-get', 'userInfo');
        setUserInfo(userinfo);
      } else {
        initializeWebUserInfo();
        setUserInfo(getWebUserInfo().userInfo);
      }
    };

    fetchData();
  }, []);

  const handleLoad = useCallback(async () => {
    if (!closingDate) {
      toast.error('Date is required');
      return;
    }
    setLoading(true);
    try {
      const headers = getTokenHeaders(token);
      const [denRes, sumRes, closingRes] = await Promise.all([
        axios.get(`${BASE_URL}${GET_DENOMINATIONS}`, { headers }),
        axios.get(`${BASE_URL}${GET_COLLECTION_SUMMARY}`, {
          headers,
          params: { fromDate: closingDate, toDate: closingDate },
        }),
        axios.get(`${BASE_URL}${POST_DAY_CLOSE}`, {
          headers,
          params: { fromDate: closingDate, toDate: closingDate },
        }),
      ]);

      const baseDenominations = normalizeDenominations(denRes);
      let nextDenominations = baseDenominations;
      let nextShift1Denominations = baseDenominations.map((row) => ({ ...row, quantity: 0, amount: 0 }));
      let nextHasShift1Closing = false;
      let nextShift1BankAmount = 0;

      const summaryRoot = sumRes?.data;
      const summaryFailed = isApiFailed(summaryRoot);
      const data = summaryRoot?.data ?? summaryRoot;

      let nextCollectionSummary = summaryFailed ? [] : buildCollectionSummaryDetails(data);
      const { systemCashAmount, systemBankAmount } = readReceiptSystemAmounts(data);
      let nextFormValues = {
        bankAmount: systemBankAmount,
        systemCashAmount,
        systemBankAmount,
        systemCardAmount: 0,
        cardAmount: 0,
        remarks: '',
      };

      if (summaryFailed) {
        toast.warn(summaryRoot?.message || summaryRoot?.Message || 'Collection summary unavailable');
      }

      const userID = Number(userInfo?.UserID);
      const closings = normalizeClosings(closingRes);
      const matchedShift1Closing = Number.isFinite(userID)
        ? (findClosingByShift(closings, userID, 1) || findMatchingClosing(closings, userID))
        : null;

      if (matchedShift1Closing) {
        const detailRes = await axios.get(
          `${BASE_URL}${POST_DAY_CLOSE}/${Number(matchedShift1Closing?.ClosingID)}`,
          { headers }
        );
        const detailRoot = detailRes?.data;
        if (!isApiFailed(detailRoot)) {
          const header = Array.isArray(detailRoot?.data?.header) ? detailRoot.data.header[0] : null;
          const details = Array.isArray(detailRoot?.data?.details) ? detailRoot.data.details : [];
          nextShift1Denominations = prefillDenominationsFromDetails(baseDenominations, details);
          nextDenominations = baseDenominations.map((row) => ({ ...row, quantity: 0, amount: 0 }));

          const parsedSummary = parseCollectionSummaryJson(
            header?.CollectionSummaryDetailsJson ?? matchedShift1Closing?.CollectionSummaryDetailsJson
          );
          if (parsedSummary.length > 0) nextCollectionSummary = parsedSummary;

          // Previous closing bank stays in shift1BankAmount; current input is only the new-shift delta
          // (same pattern as cash denoms: prev + current → submit total).
          nextShift1BankAmount = Number(header?.BankAmount ?? matchedShift1Closing?.BankAmount ?? 0) || 0;
          const liveSystemBankAmount = Number(nextFormValues.systemBankAmount || 0) || 0;
          nextFormValues = {
            bankAmount: Math.max(0, liveSystemBankAmount - nextShift1BankAmount),
            systemCashAmount:
              Number(
                header?.SystemCashAmount ??
                  matchedShift1Closing?.SystemCashAmount ??
                  nextFormValues.systemCashAmount
              ) || 0,
            // Keep live day bank/UPI from collection-summary (do not freeze at previous closing)
            systemBankAmount: liveSystemBankAmount,
            systemCardAmount:
              Number(
                header?.SystemCardAmount ??
                  matchedShift1Closing?.SystemCardAmount ??
                  nextFormValues.systemCardAmount
              ) || 0,
            cardAmount: 0,
            remarks: header?.Remarks ?? matchedShift1Closing?.Remarks ?? '',
          };
          nextHasShift1Closing = true;
          toast.success('Shift-1 closing loaded for current user');
        } else {
          nextDenominations = baseDenominations.map((row) => ({ ...row, quantity: 0, amount: 0 }));
          nextShift1Denominations = baseDenominations.map((row) => ({ ...row, quantity: 0, amount: 0 }));
          toast.warn(detailRoot?.message || detailRoot?.Message || 'Closing details unavailable');
        }
      } else if (Number.isFinite(userID)) {
        nextDenominations = baseDenominations.map((row) => ({ ...row, quantity: 0, amount: 0 }));
        nextShift1Denominations = baseDenominations.map((row) => ({ ...row, quantity: 0, amount: 0 }));
        toast.info('No existing closing found for this user on selected date');
      }

      setDenominations(nextDenominations);
      setShift1Denominations(nextShift1Denominations);
      setCollectionSummaryDetails(nextCollectionSummary);
      setShift1BankAmount(nextShift1BankAmount);
      setHasShift1Closing(nextHasShift1Closing);
      setForm((prev) => ({
        ...prev,
        ...nextFormValues,
      }));
      setSessionLoaded(baseDenominations.length > 0);
    } catch (error) {
      toast.error('Could not load denominations or collection summary');
      setDenominations([]);
      setShift1Denominations([]);
      setCollectionSummaryDetails([]);
      setShift1BankAmount(0);
      setHasShift1Closing(false);
      setSessionLoaded(false);
    } finally {
      setLoading(false);
    }
  }, [token, closingDate, userInfo?.UserID]);

  const totalShift1CashAmount = useMemo(
    () => shift1Denominations.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [shift1Denominations]
  );

  const totalShift2CashAmount = useMemo(
    () => denominations.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [denominations]
  );

  const totalCashAmount = totalShift1CashAmount + totalShift2CashAmount;
  const totalBankAmount = Number(shift1BankAmount || 0) + Number(form.bankAmount || 0);

  const bankDifference = totalBankAmount - Number(form.systemBankAmount || 0);
  const cardDifference = Number(form.cardAmount || 0) - Number(form.systemCardAmount || 0);
  const cashDifference = totalCashAmount - Number(form.systemCashAmount || 0);

  const handleDenominationQtyChange = (index, qtyValue) => {
    const parsedQty = Number(qtyValue);
    const quantity = Number.isFinite(parsedQty) && parsedQty >= 0 ? parsedQty : 0;
    setDenominations((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              quantity,
              amount: Number(row.denominationValue) * quantity,
            }
          : row
      )
    );
  };

  const buildPayload = () => {
    const combinedDenominations = denominations.map((row, index) => {
      const shift1Row = shift1Denominations[index] || {};
      const totalQuantity = Number(shift1Row.quantity || 0) + Number(row.quantity || 0);
      return {
        denominationID: Number(row.denominationID || 0),
        denominationValue: Number(row.denominationValue || 0),
        quantity: totalQuantity,
        amount: Number(row.denominationValue || 0) * totalQuantity,
      };
    });

    return {
      docStatus: 'O',
      closingID: null,
      editNo: null,
      closingDate,
      counterID: 1,
      shiftID: hasShift1Closing ? 2 : 1,
      totalCashAmount: Number(totalCashAmount || 0),
      bankAmount: Number(totalBankAmount || 0),
      cardAmount: Number(form.cardAmount || 0),
      systemCashAmount: Number(form.systemCashAmount || 0),
      systemBankAmount: Number(form.systemBankAmount || 0),
      systemCardAmount: Number(form.systemCardAmount || 0),
      cashDifference: Number(cashDifference || 0),
      bankDifference: Number(bankDifference || 0),
      cardDifference: Number(cardDifference || 0),
      remarks: form.remarks?.trim() ? form.remarks.trim() : null,
      denominationDetails: combinedDenominations,
      collectionSummaryDetails,
    };
  };

  const validate = () => {
    if (!sessionLoaded) {
      toast.error('Click Generate to fetch session data');
      return false;
    }
    if (!closingDate) {
      toast.error('Date is required for closing');
      return false;
    }
    if (denominations.length === 0) {
      toast.error('No active denominations available');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const headers = {
        XApiKey: process.env.REACT_APP_API_KEY,
        Authorization: `Bearer ${token}`,
        DocStatus: 'O',
        EditNo: null,
        ClsID: null,
      };
      const response = await axios.post(`${BASE_URL}${POST_DAY_CLOSE}`, payload, { headers });
      if (response.status === 200 || response.status === 201) {
        toast.success('Day close saved successfully');
      } else {
        toast.error('Failed to save day close');
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.Message ||
        'Day close save failed';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const onFormField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleBankAmountChange = (value) => {
    const parsed = parseFloat(value);
    if (value !== '' && !isNaN(parsed) && parsed < 0) {
      toast.error('Bank/UPI amount cannot be negative.', { autoClose: 1000 });
      return;
    }
    onFormField('bankAmount', value === '' ? '' : (isNaN(parsed) ? 0 : parsed));
  };

  return (
    <div className="flex h-full bg-background">
      <ToastContainer
        position="top-center"
        autoClose={1500}
        limit={2}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Slide}
      />

      {(loading || submitting) && (
        <Spinner label={submitting ? 'Saving day close...' : 'Loading data...'} />
      )}

      <div className="flex-1 flex h-full flex-col overflow-hidden hide-scrollbar">
        <div className="bg-white p-3 mb-2">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-bold text-black flex items-center">Day Close</h1>
              <p className="text-xs text-black">
                Load sales and receipt totals, then enter cash denominations and bank on hand
              </p>
            </div>
            <div className="bg-white rounded-xl px-5 py-2 border border-gray-200 ">
            <div className="flex items-center gap-8">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-xs font-medium text-slate-700">
                  Date
                  <input
                    type="date"
                    value={closingDate}
                    onChange={(e) => setClosingDate(e.target.value)}
                    className="rounded-lg border border-slate-200 bg-white cursor-pointer px-2 py-1.5 text-xs text-slate-700 shadow-sm focus:border-indigo-400 focus:ring-2 focus:ring-indigo-200 transition"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={handleLoad}
                className="inline-flex items-center gap-2  rounded-full bg-indigo-500 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-600"
              >
                <FiBarChart2 size={16} />
                Generate
              </button>
            </div>
          </div>
          </div>
        </div>

        <div className="p-2 overflow-auto">
         

          <div className="bg-white rounded-xl shadow-md p-5 mb-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Denomination Entry</h2>
            <p className="text-xs text-gray-500 mb-4">
              {hasShift1Closing
                ? 'Shift-1 closing is shown as read-only. Enter Shift-2 cash denoms and remaining Bank/UPI; totals are combined on submit.'
                : 'No Shift-1 closing found. Enter Shift-1 denominations and Bank/UPI amount.'}
            </p>
            <div className="overflow-x-auto border border-gray-700 rounded-md">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 border-b border-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Denomination
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Shift-1 Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Shift-1 Amount
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Shift-2 Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Shift-2 Amount
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Total Qty
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                      Total Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {denominations.map((row, index) => (
                    <tr key={`${row.denominationID}-${index}`} className="border-b border-gray-700">
                      <td className="px-4 py-2 text-sm font-medium text-indigo-700 border-r border-gray-700">
                        {row.denominationValue}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-700">
                        {Number(shift1Denominations[index]?.quantity || 0)}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-700">
                        {Number(shift1Denominations[index]?.amount || 0)}
                      </td>
                      <td className="px-4 py-2 border-r border-gray-700">
                        <input
                          type="number"
                          min={0}
                          className="w-32 border border-gray-500 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          value={row.quantity}
                          onChange={(e) => handleDenominationQtyChange(index, e.target.value)}
                        />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-700">{row.amount}</td>
                      <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-700">
                        {Number(shift1Denominations[index]?.quantity || 0) + Number(row.quantity || 0)}
                      </td>
                      <td className="px-4 py-2 text-sm font-medium text-indigo-700">
                        {Number(shift1Denominations[index]?.amount || 0) + Number(row.amount || 0)}
                      </td>
                    </tr>
                  ))}
                  {denominations.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        Click Generate to fetch denominations for this session.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-indigo-50 border-t border-gray-700">
                    <td colSpan={2} className="px-4 py-2 text-sm font-semibold text-gray-800 border-r border-gray-700">
                      Total Cash Amount
                    </td>
                    <td className="px-4 py-2 text-sm font-bold text-indigo-700" colSpan={5}>
                      {totalCashAmount}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* <div className="bg-white rounded-xl shadow-md p-5 mb-5">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">Tender Summary</h2>
            <p className="text-xs text-gray-500 mb-4">
              {hasShift1Closing
                ? 'Shift-1 Bank/UPI is read-only from the previous closing. Enter Shift-2 Bank/UPI; totals are combined on submit.'
                : 'Enter Bank/UPI on hand for this shift.'}
            </p>

            <div className="overflow-x-auto border border-gray-700 rounded-md mb-4">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 border-b border-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Tender
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Shift-1 Amount
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase border-r border-gray-700">
                      Shift-2 Amount
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase">
                      Total Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-700">
                    <td className="px-4 py-2 text-sm font-medium text-indigo-700 border-r border-gray-700">
                      Cash
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-700">
                      {totalShift1CashAmount}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-700">
                      {totalShift2CashAmount}
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-indigo-700">{totalCashAmount}</td>
                  </tr>
                  <tr className="border-b border-gray-700">
                    <td className="px-4 py-2 text-sm font-medium text-indigo-700 border-r border-gray-700">
                      Bank/UPI
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-700 border-r border-gray-700">
                      {Number(shift1BankAmount || 0)}
                    </td>
                    <td className="px-4 py-2 border-r border-gray-700">
                      <input
                        type="number"
                        min={0}
                        className="w-32 border border-gray-500 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        value={form.bankAmount}
                        onChange={(e) => handleBankAmountChange(e.target.value)}
                        title="Enter Bank/UPI amount on hand for this shift"
                      />
                    </td>
                    <td className="px-4 py-2 text-sm font-medium text-indigo-700">{totalBankAmount}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Remarks</label>
                <input
                  type="text"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5"
                  value={form.remarks}
                  onChange={(e) => onFormField('remarks', e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500">System cash (from receipts)</p>
                <p className="text-sm font-semibold text-gray-800">{form.systemCashAmount}</p>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500">System bank/UPI (from receipts)</p>
                <p className="text-sm font-semibold text-gray-800">{form.systemBankAmount}</p>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500">System card</p>
                <p className="text-sm font-semibold text-gray-800">{form.systemCardAmount}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500">Cash difference</p>
                <p className="text-sm font-semibold text-gray-800">{cashDifference}</p>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500">Bank/UPI difference</p>
                <p className="text-sm font-semibold text-gray-800">{bankDifference}</p>
              </div>
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-500">Card difference</p>
                <p className="text-sm font-semibold text-gray-800">{cardDifference}</p>
              </div>
            </div>

            {hasShift1Closing && (
              <p className="text-xs text-indigo-600 mt-3 font-medium">
                Closing as Shift-2 — Shift-1 cash and Bank/UPI will be combined on submit.
              </p>
            )}
          </div> */}

          <div className="flex justify-end">
            <button
              type="button"
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-sm rounded-lg shadow-md hover:shadow-lg transition-all flex items-center gap-2"
              onClick={handleSubmit}
              disabled={submitting}
            >
              <FiSave />
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withAuth(DayClose);
